import React from 'react';
import { SavedQuery } from '@/types';
import { DASHBOARD_TEXT_FILTER_OPTION_INPUT } from '@/constants/test-ids';
import { GeneralStylesWidgetContainer } from '@/features/savedQueriesWidget/GeneralStylesWidgetContainer';
import { SavedQueriesWidget } from '@/features/savedQueriesWidget/SavedQueriesWidget';
import { SAVE_MODAL } from '@/features/savedQueriesWidget/widgetRenderContext.constants';
import { useGetSavedQueries } from '@/features/savedQueriesWidget/apiSlice';
import AjaxSpinner from '@/components/AjaxSpinner';
import {
  TSavedQueryPreset,
  TFilterPresetsType,
  FILTER_PRESETS_TYPE,
} from './types';
import { SavedQueryConfiguration } from './SavedQueryConfiguration';
import { LabelsContainer } from './PresetChoices.styles';

export type PresetChoicesProps = {
  value: string;
  onChange: React.ChangeEventHandler<HTMLTextAreaElement>;
  setSavedQueryPresets: F<Maybe<TSavedQueryPreset>>;
  savedQueryPresets: Maybe<TSavedQueryPreset>;
};

export const PresetChoices: React.FC<
  PresetChoicesProps & {
    presetsType: TFilterPresetsType;
    setPresetsType: F<TFilterPresetsType>;
    preselectedSavedQuery: Maybe<SavedQuery>;
  }
> = ({
  value,
  onChange,
  setSavedQueryPresets,
  presetsType,
  setPresetsType,
  preselectedSavedQuery,
  savedQueryPresets,
}) => {
  const [selectedSavedQuery, setSelectedSavedQuery] = React.useState<
    Maybe<SavedQuery>
  >(preselectedSavedQuery);

  const handleReset = () => {
    setSelectedSavedQuery(null);
    setSavedQueryPresets(null);
  };

  return (
    <div className="margin-bottom-25">
      <h4>Preset choices (optional)</h4>

      {selectedSavedQuery ? (
        <SavedQueryConfiguration
          setSavedQueryPresets={setSavedQueryPresets}
          savedQuery={selectedSavedQuery}
          savedQueryPresets={savedQueryPresets}
          reset={handleReset}
        />
      ) : (
        <>
          <LabelsContainer>
            <label htmlFor={FILTER_PRESETS_TYPE.manual}>
              <input
                id={FILTER_PRESETS_TYPE.manual}
                type="radio"
                checked={presetsType === FILTER_PRESETS_TYPE.manual}
                onChange={() => setPresetsType(FILTER_PRESETS_TYPE.manual)}
              />
              Manually add preset choices
            </label>
            <label htmlFor={FILTER_PRESETS_TYPE.prefilled}>
              <input
                id={FILTER_PRESETS_TYPE.prefilled}
                type="radio"
                checked={presetsType === FILTER_PRESETS_TYPE.prefilled}
                onChange={() => setPresetsType(FILTER_PRESETS_TYPE.prefilled)}
              />
              Prefill choices from a saved query
            </label>
          </LabelsContainer>

          {presetsType === FILTER_PRESETS_TYPE.manual && (
            <textarea
              placeholder="One per line..."
              data-testid={DASHBOARD_TEXT_FILTER_OPTION_INPUT}
              data-gramm="false"
              onChange={onChange}
              value={value}
              style={{ maxWidth: '300px' }}
              rows={5}
              className="form-control"
            />
          )}

          {presetsType === FILTER_PRESETS_TYPE.prefilled && (
            <GeneralStylesWidgetContainer>
              <SavedQueriesWidget
                onSelectQuery={setSelectedSavedQuery}
                mode={SAVE_MODAL}
              />
            </GeneralStylesWidgetContainer>
          )}
        </>
      )}
    </div>
  );
};

export const PresetChoicesContainer: React.FC<
  PresetChoicesProps & {
    presetsType: TFilterPresetsType;
    setPresetsType: F<TFilterPresetsType>;
  }
> = ({ presetsType, setPresetsType, ...props }) => {
  const { data, isFetching } = useGetSavedQueries();

  if (isFetching) return <AjaxSpinner />;

  const preselectedSavedQuery =
    data?.savedQueries.find(
      (sq) => sq.series === props.savedQueryPresets?.seriesId,
    ) ?? null;

  return (
    <PresetChoices
      {...props}
      presetsType={presetsType}
      setPresetsType={setPresetsType}
      preselectedSavedQuery={preselectedSavedQuery}
    />
  );
};
